import { Button, message } from "antd";
import { Form, Formik } from "formik";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import usePostData from "../../../api/usePostData";
import AppInput from "../../../common/AppInput";
import AuthFooter from "../../../components/AuthFooter/AuthFooter";
import Recaptcha from "../../../components/Recaptcha/Recaptcha";
import Stepper from "../../../components/Stepper/Stepper";
import { registerAddressInformation } from "../../../utils/validations";
import { RegisterFormContext } from "./RegisterFormContext";

/**
 * A component for the user's address info step in the registeration process.
 * It gets shown only when the current user is an agent, not a guest user creating a new account.
 *
 * @returns {JSX.Element}
 */

const CityInfoStep = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("global");
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const { setActiveStep, steps, email, activeStep, phone, userInfo } =
    useContext(RegisterFormContext);
  const { postData, errors } = usePostData({});
  const onSubmit = async (values) => {
    if (!recaptchaToken) {
      return message.error(t("recaptchaRequired"));
    }
    const response = await postData({
      route: "api/method/airport_transport.api.user.register",
      data: {
        email,
        phone,
        first_name: userInfo.firstName,
        last_name: userInfo.lastName,
        dob: userInfo.dob,
        nationality: userInfo.nationality,
        gender: userInfo.gender,
        city: values.city,
        state: values.state,
        postcode: values.postcode,
        street: values.street,
      },
      params: {
        headers: {
          recaptchaToken,
        },
      },
    });
    if (response && response.status === 200) {
      const msg = response?.data?.msg?.toLowerCase();
      if (msg === "user created successfully") {
        message.success(t("user_created_successfully"));
      } else {
        message.success(`${msg}`);
      }
      navigate("/");
    }
  };

  useEffect(() => {
    if (errors) {
      const errorMsg = errors?.error || "";
      if (errorMsg.toLowerCase() === "mobile number must be unique") {
        message.error(t("errors.mobile_number_must_be_unique"));
      } else {
        message.error(`${errorMsg}`);
      }
    }
  }, [errors]);

  return (
    <div className="sm:w-screen w-full position relative">
      <div className="-z-10 position absolute left-0 top-0">
        <img
          src="./assets/signin/left_vector.png"
          alt="left_vector"
          className="w-20 h-20 md:w-48 md:h-48"
        />
      </div>
      <div className="-z-10 position absolute right-0 bottom-0">
        <img
          src="./assets/signin/right_vector.png"
          alt="right_vector"
          className="w-16 md:w-48 h-12 md:h-36"
        />
      </div>
      <div className="sm:w-screen w-full min-h-screen overflow-auto">
        <div className="flex flex-col justify-center items-center">
          <button
            className="mt-4 mb-2 cursor-pointer"
            onClick={() => navigate("/")}
          >
            <img
              src="./assets/header/LogoSVG.svg"
              alt="Moshrouk Trips"
              className="w-16 h-13"
            />
          </button>
          <div className="bg-text_white  mt-2 block w-72 md:w-[480px] p-3 bg-white border border-gray-200 rounded-lg shadow hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 md:max-w-[500px]">
            <div className="w-auto p-1">
              <div>
                <Stepper
                  steps={steps}
                  subTab={activeStep}
                  className={
                    "flex items-center w-full text-sm font-medium text-center py-2 border-b text-gray-500 sm:text-base justify-between"
                  }
                />
              </div>
              <Formik
                initialValues={{
                  city: "",
                  state: "",
                  postcode: "",
                  street: "",
                }}
                validationSchema={registerAddressInformation}
                onSubmit={onSubmit}
              >
                {({ errors, touched }) => (
                  <Form className="mx-auto w-full">
                    <div className="flex flex-col  md:flex-row justify-between items-center">
                      <div className="w-full md:w-1/2 mr-0 md:mr-1">
                        <AppInput
                          name="city"
                          label={t("my_profile.city_text")}
                          type="text"
                          placeholder={t("my_profile.city_text")}
                          error={errors.city}
                          touched={touched.city}
                        />
                      </div>
                      <div className="w-full md:w-1/2 ml-0 md:ml-1">
                        <AppInput
                          name="state"
                          label={t("my_profile.state_text")}
                          placeholder={t("my_profile.state_text")}
                          type="text"
                          error={errors.state}
                          touched={touched.state}
                        />
                      </div>
                    </div>
                    <div>
                      <AppInput
                        name="postcode"
                        label={t("my_profile.postal_text")}
                        placeholder={t("my_profile.postal_text")}
                        type="text"
                        error={errors.postcode}
                        touched={touched.postcode}
                      />
                    </div>
                    <div>
                      <AppInput
                        name="street"
                        label={t("my_profile.street_address_text")}
                        placeholder={t("my_profile.street_address_text")}
                        type="text"
                        error={errors.street}
                        touched={touched.street}
                      />
                    </div>
                    <div>
                      <Recaptcha
                        onChange={(value) => {
                          setRecaptchaToken(value);
                        }}
                      />
                    </div>
                    <div className="w-full mt-3 flex flex-col md:flex-row justify-between items-center">
                      <Button
                        className="bg-bg_btn_back cursor-pointer w-full md:w-1/2 text-text_white hover:bg-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 mb-2 me-0 md:me-2"
                        onClick={() => {
                          setActiveStep(1);
                        }}
                        type="button"
                        htmlType="button"
                      >
                        {t("previous_text")}
                      </Button>
                      <Button
                        className="bg-background_steel_blue cursor-pointer w-full md:w-1/2 text-text_white hover:bg-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 mb-2 me-0 md:me-2"
                        type="submit"
                        htmlType="submit"
                      >
                        {t("submit_text")}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
          <div className="">
            <AuthFooter />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CityInfoStep;
